import { openLucasDb } from "./lucasDb";

// An empty queue is ambiguous: a save may have removed its rows, or the recorder
// may have disappeared before writing them. Retain a small, account-scoped receipt
// of acknowledged uploads so a reloaded document can tell those cases apart.
const RECEIPT_PREFIX = "webRecordingSaved:";
const RECEIPT_LIMIT = 100;
const RECEIPT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const validIdentity = ({ recordingId, userId, orgId } = {}) =>
  typeof recordingId === "string" && !!recordingId && !!userId && typeof orgId === "string" && !!orgId;
const receiptKey = ({ userId, orgId }) => RECEIPT_PREFIX + JSON.stringify([userId, orgId]);

function readReceipts(identity) {
  const parsed = JSON.parse(localStorage.getItem(receiptKey(identity)) || "{}");
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
}

const receiptTime = (receipt) => (typeof receipt === "number" ? receipt : receipt?.savedAt);

export function recordWebRecordingSaved(identity, { complete = true } = {}) {
  if (!validIdentity(identity)) return false;
  try {
    const now = Date.now();
    const receipts = readReceipts(identity);
    for (const id of Object.keys(receipts)) {
      const savedAt = receiptTime(receipts[id]);
      if (!Number.isFinite(savedAt) || now - savedAt > RECEIPT_TTL_MS) delete receipts[id];
    }
    receipts[identity.recordingId] = { savedAt: now, complete: complete === true };
    const ids = Object.keys(receipts).sort((a, b) => receiptTime(receipts[a]) - receiptTime(receipts[b]));
    for (const id of ids.slice(0, Math.max(0, ids.length - RECEIPT_LIMIT))) delete receipts[id];
    localStorage.setItem(receiptKey(identity), JSON.stringify(receipts));
    const stored = readReceipts(identity)[identity.recordingId];
    return stored?.savedAt === now && stored.complete === (complete === true);
  } catch (_) {
    // Receipts are supplementary proof. Storage failure must never fabricate a
    // saved result; a later inspection can still use the actual audio rows.
    return false;
  }
}

const hasSegmentBytes = (audio) => {
  if (typeof audio !== "string") return false;
  const comma = audio.indexOf(",");
  return comma >= 0 && audio.slice(comma + 1).trim().length > 0;
};

/**
 * Inspect committed custody, never recorder state or elapsed time. Called only
 * after obtaining exclusive recording ownership, so another live recorder cannot
 * still be filling the candidate set. Audio stays in IndexedDB; only indices and
 * counts are retained during the cursor scan.
 */
export async function inspectWebRecordingCustody(identity) {
  if (!validIdentity(identity)) return { status: "unknown" };
  try {
    const receipt = readReceipts(identity)[identity.recordingId];
    const savedAt = receiptTime(receipt);
    if (Number.isFinite(savedAt) && Date.now() - savedAt >= 0 && Date.now() - savedAt <= RECEIPT_TTL_MS) {
      // A finalless/partial recovery did save a transcript, but it cannot prove
      // that the lost capture's tail arrived. It still requires user review.
      return { status: receipt?.complete === true ? "complete" : "recoverable", source: "uploaded" };
    }
  } catch (_) {
    // A failed receipt read cannot disprove a complete local copy.
  }

  try {
    const db = await openLucasDb();
    if (!db) return { status: "unknown" };
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(["recordings"], "readonly");
      const request = tx.objectStore("recordings").index("userId").openCursor(IDBKeyRange.only(identity.userId));
      const present = new Set();
      const finalTotals = new Set();
      let hasBlob = false;
      let hasFinal = false;
      let invalidFinal = false;
      let rowCount = 0;
      request.onsuccess = (event) => {
        try {
          const cursor = event.target.result;
          if (!cursor) return;
          const row = cursor.value;
          if (row?.recordingId === identity.recordingId && row.orgId === identity.orgId) {
            rowCount += 1;
            if (row.kind !== "segment") {
              hasBlob = hasBlob || row.blobData?.byteLength > 0 || row.blob?.size > 0;
            } else {
              if (Number.isInteger(row.index) && row.index >= 0 && hasSegmentBytes(row.segmentAudio)) present.add(row.index);
              if (row.isFinal) {
                hasFinal = true;
                if (Number.isInteger(row.total) && row.total >= 0) finalTotals.add(row.total);
                else invalidFinal = true;
              }
            }
          }
          cursor.continue();
        } catch (error) {
          reject(error);
        }
      };
      request.onerror = (event) => reject(event.target?.error || event);
      tx.onerror = (event) => reject(tx.error || event.target?.error || event);
      tx.onabort = (event) => reject(tx.error || event.target?.error || event);
      tx.oncomplete = () => {
        const expectedTotal = finalTotals.size === 1 ? [...finalTotals][0] : null;
        // An empty final row is valid metadata, but never substitutes for any of
        // the audio indices. Repeated rows cannot hide a missing middle segment.
        let complete = !invalidFinal && expectedTotal > 0 && present.size === expectedTotal;
        if (complete) {
          for (let index = 0; index < expectedTotal; index++) {
            if (!present.has(index)) {
              complete = false;
              break;
            }
          }
        }
        resolve({
          status: hasBlob || complete ? "complete" : present.size > 0 ? "recoverable" : "missing",
          source: "local",
          segmentCount: present.size,
          expectedTotal,
          hasFinal,
          hasRows: rowCount > 0,
        });
      };
    });
  } catch (_) {
    return { status: "unknown" };
  }
}
